import { Box, Flex, Text } from '@chakra-ui/react'
import {
  LuTriangleAlert as AlertTriangle,
  LuLandmark as Landmark,
  LuRefreshCw as RefreshCw,
  LuFileText as FileText,
  LuDownload as Download,
} from 'react-icons/lu'
import type { MiningRightResponse } from '../api/models/response/proveedor'
import { proveedoresService } from '../api/services'

interface IngemmetOfficialPanelProps {
  right: MiningRightResponse
  /** Cuando se provee, el panel ofrece consultar o actualizar contra INGEMMET. */
  onImport?: () => void | Promise<void>
  importing?: boolean
  /**
   * Cuando se provee, el panel ofrece traer el expediente digitalizado. Va aparte del
   * resto: son ~200 peticiones a INGEMMET y unos 30 MB, así que se hace a pedido y no
   * junto con los datos, que son dos llamadas y se traen solos.
   */
  onDescargarExpediente?: () => void | Promise<void>
  /** Progreso de la descarga en curso, o null si no hay ninguna. */
  progresoExpediente?: { descargadas: number; total: number } | null
}

/**
 * Lo que INGEMMET dice del derecho minero, en su propio bloque.
 *
 * Es un eje distinto del "progreso de registro" que está arriba en la tarjeta: ese mide lo
 * que le falta cargar al usuario, y esto es verificación contra el registro oficial. Nada
 * de acá lo declara el minero.
 *
 * Los dos datos que cambian decisiones:
 *   - El ESTADO oficial, que puede contradecir al estado declarado. Un derecho extinguido o
 *     bloqueado no se puede usar por más que el minero figure en proceso de formalización.
 *   - El TITULAR, que rara vez es el minero. Cuando no lo es, el minero opera el derecho de
 *     un tercero y hay que exigirle el contrato de cesión.
 */
export default function IngemmetOfficialPanel({
  right,
  onImport,
  importing = false,
  onDescargarExpediente,
  progresoExpediente,
}: IngemmetOfficialPanelProps) {
  const nuncaConsultado = right.ingemmetCheckedAt == null
  const noEncontrado = right.ingemmetFound === false

  const boton = onImport ? (
    <Box
      as="button"
      {...{ type: 'button', disabled: importing }}
      display="inline-flex"
      alignItems="center"
      gap={1.5}
      px={3}
      py={1}
      borderRadius="md"
      border="1px solid #cbd5e0"
      bg="white"
      color="#2d3748"
      fontSize="11px"
      fontWeight="700"
      cursor={importing ? 'default' : 'pointer'}
      opacity={importing ? 0.6 : 1}
      _hover={importing ? {} : { bg: '#edf2f7' }}
      onClick={() => { if (!importing) void onImport() }}
    >
      <RefreshCw size={12} />
      {importing ? 'Consultando...' : nuncaConsultado ? 'Consultar INGEMMET' : 'Actualizar'}
    </Box>
  ) : null

  // Nunca se consultó: no hay nada que mostrar más que la invitación a hacerlo.
  if (nuncaConsultado) {
    return (
      <Box mt={4} p={4} borderRadius="xl" border="1px dashed #cbd5e0" bg="#f7fafc">
        <Flex gap={3} alignItems="center" flexWrap="wrap">
          <Landmark size={18} color="#857b71" />
          <Box flex={1} minW="200px">
            <Text fontSize="13px" fontWeight="700" color="#2d3748">Registro oficial INGEMMET</Text>
            <Text fontSize="12px" color="#857b71" mt={0.5}>
              Sin consultar. Trae el titular, el estado oficial y el polígono del derecho minero.
            </Text>
          </Box>
          {boton}
        </Flex>
      </Box>
    )
  }

  // El código no existe en el catastro. No es un fallo del servicio: es un código mal
  // cargado, y hay que corregirlo antes de aprobar el derecho.
  if (noEncontrado) {
    return (
      <Box mt={4} p={4} borderRadius="xl" border="1px solid #feb2b2" bg="#fff5f5">
        <Flex gap={3} alignItems="flex-start" flexWrap="wrap">
          <AlertTriangle size={18} color="#9b2c2c" style={{ flexShrink: 0, marginTop: 2 }} />
          <Box flex={1} minW="200px">
            <Text fontSize="13px" fontWeight="700" color="#9b2c2c">
              El código {right.uniqueCode} no existe en INGEMMET
            </Text>
            <Text fontSize="12px" color="#9b2c2c" mt={0.5}>
              No se pudo verificar el derecho contra el catastro oficial. Revisa que el código
              esté bien escrito antes de aprobarlo.
            </Text>
          </Box>
          {boton}
        </Flex>
      </Box>
    )
  }

  const holderMismatch = right.concessionHolderIsSupplier === false
  const varios = (right.concessionHolderCount ?? 1) > 1
  const cautelar = right.ingemmetPrecautionaryMeasure

  const expediente = (
    <SeccionExpediente right={right} onDescargar={onDescargarExpediente} progreso={progresoExpediente} />
  )

  return (
    <Box mt={4} borderRadius="xl" border="1px solid #e8dfd1" bg="white" overflow="hidden">
      <Flex px={4} py={2.5} bg="#f7fafc" alignItems="center" gap={2} flexWrap="wrap">
        <Landmark size={15} color="#4a5568" />
        <Text fontSize="11px" fontWeight="700" color="#4a5568" textTransform="uppercase" letterSpacing="0.06em">
          Registro oficial INGEMMET
        </Text>
        <Box flex={1} />
        {right.ingemmetCheckedAt && (
          <Text fontSize="10px" color="#a0aec0">
            {new Date(right.ingemmetCheckedAt).toLocaleDateString('es-PE')}
          </Text>
        )}
        {boton}
      </Flex>

      <Box px={4} py={3}>
        <Dato etiqueta="Estado">
          {right.ingemmetStatusName ?? '—'}
          {right.ingemmetStatusDetail && (
            <Text as="span" color="#857b71"> · {right.ingemmetStatusDetail}</Text>
          )}
        </Dato>

        <Dato etiqueta="Titular">
          {right.concessionHolderName ?? '—'}
          {varios && (
            <Text as="span" color="#857b71"> · {right.concessionHolderCount} titulares</Text>
          )}
          {right.concessionHolderSharePercent != null && !varios && (
            <Text as="span" color="#857b71"> · {right.concessionHolderSharePercent}%</Text>
          )}
        </Dato>

        {holderMismatch && (
          // El caso habitual, y el que obliga a pedir el contrato de cesión. Se marca como
          // aviso y no como error: operar el derecho de un tercero es legítimo.
          <Flex gap={1.5} alignItems="center" mt={1} mb={2} ml={{ base: 0, md: '120px' }}>
            <AlertTriangle size={12} color="#a8740e" />
            <Text fontSize="11px" fontWeight="600" color="#a8740e">
              El minero no es el titular de esta concesión
            </Text>
          </Flex>
        )}

        {right.concessionHolderAddress && (
          <Dato etiqueta="Domicilio">
            {right.concessionHolderAddress}
            {right.concessionHolderLocation && (
              <Text as="span" color="#857b71"> · {right.concessionHolderLocation}</Text>
            )}
          </Dato>
        )}

        {right.officialHectares != null && (
          <Dato etiqueta="Hectáreas">{right.officialHectares.toLocaleString('es-PE')} ha del derecho minero</Dato>
        )}

        {/*
          Los dos polígonos son cosas distintas y conviene decirlo donde el usuario los
          carga: el derecho minero es el contorno completo que publica INGEMMET, y la
          concesión es el área que ESTE minero trabaja adentro. Un mismo derecho puede
          estar repartido entre varios mineros.
        */}
        <Dato etiqueta="Polígonos">
          <Flex gap={1.5} alignItems="center" flexWrap="wrap">
            {right.hasOfficialBoundary
              ? <Text as="span" color="#1d6664">Derecho minero: importado</Text>
              : <Text as="span" color="#a0aec0">Derecho minero: sin polígono en el catastro</Text>}
            <Text as="span" color="#cbd5e0">·</Text>
            {right.hasBoundary
              ? <Text as="span" color="#1d6664">Concesión: cargada</Text>
              : <Text as="span" color="#a8740e">Concesión: falta dibujarla</Text>}
          </Flex>
          {right.boundaryFromIngemmet === true && (
            <Text fontSize="11px" color="#a8740e" mt={1}>
              La concesión se copió del contorno del derecho minero. Si el minero trabaja
              solo una parte, conviene dibujar su área real.
            </Text>
          )}
          {/*
            Es la razon de traer el contorno oficial: sin el no habia contra que
            contrastar lo declarado. En dev aparecieron concesiones a casi 1.000 km de su
            propio derecho minero.
          */}
          {right.concessionWithinOfficial === false && (
            <Flex gap={1.5} alignItems="center" mt={1}>
              <AlertTriangle size={12} color="#9b2c2c" style={{ flexShrink: 0 }} />
              <Text fontSize="11px" fontWeight="600" color="#9b2c2c">
                La concesión declarada no cae dentro del derecho minero
              </Text>
            </Flex>
          )}
        </Dato>

        {cautelar && (
          <Flex mt={2} p={2.5} borderRadius="md" bg="#fff5f5" border="1px solid #feb2b2" gap={2} alignItems="flex-start">
            <AlertTriangle size={14} color="#9b2c2c" style={{ flexShrink: 0, marginTop: 1 }} />
            <Box>
              <Text fontSize="11px" fontWeight="700" color="#9b2c2c">Medida cautelar</Text>
              <Text fontSize="11px" color="#9b2c2c">{cautelar}</Text>
            </Box>
          </Flex>
        )}

        {expediente}
      </Box>
    </Box>
  )
}

function SeccionExpediente({ right: r, onDescargar, progreso }: {
  right: MiningRightResponse
  onDescargar?: () => void | Promise<void>
  progreso?: { descargadas: number; total: number } | null
}) {
  const totalPaginas = r.ingemmetArchivePageCount ?? 0
  if (totalPaginas === 0) return null

  const completado = r.expedienteDownloadStatus === 4 || (r.expedientePdfPath != null)
  const enCurso = progreso != null

  return (
    <Box mt={3} pt={3} borderTop="1px solid #edf2f7">
      <Flex gap={2} alignItems="center" flexWrap="wrap">
        <FileText size={14} color="#4a5568" />
        <Text fontSize="12px" fontWeight="600" color="#2d3748">
          Expediente digitalizado · {totalPaginas} páginas
        </Text>
        <Box flex={1} />

        {completado && r.expedientePdfPath && (
          <Box as="button" {...{ type: 'button' }} display="inline-flex" alignItems="center" gap={1.5}
            px={3} py={1} borderRadius="md" bg="#b4532a" color="white"
            fontSize="11px" fontWeight="700" cursor="pointer" _hover={{ opacity: 0.9 }}
            onClick={() => void proveedoresService.downloadFile(r.expedientePdfPath!, `expediente-${r.uniqueCode}.pdf`)}
          >
            <Download size={12} /> Descargar PDF
          </Box>
        )}

        {!completado && onDescargar && (
          <Box as="button" {...{ type: 'button', disabled: enCurso }} display="inline-flex" alignItems="center" gap={1.5}
            px={3} py={1} borderRadius="md" border="1px solid #cbd5e0" bg="white" color="#2d3748"
            fontSize="11px" fontWeight="700"
            cursor={enCurso ? 'default' : 'pointer'} opacity={enCurso ? 0.6 : 1}
            _hover={enCurso ? {} : { bg: '#edf2f7' }}
            onClick={() => { if (!enCurso) void onDescargar() }}
          >
            <Download size={12} />
            {enCurso ? 'Descargando...' : 'Traer expediente'}
          </Box>
        )}
      </Flex>

      {enCurso && (
        // Son ~200 peticiones a INGEMMET: sin barra, el usuario no sabe si avanza.
        <Box mt={2}>
          <Box h="6px" borderRadius="full" bg="#edf2f7" overflow="hidden">
            <Box h="100%" bg="#b4532a" transition="width 0.3s"
              width={`${Math.round((progreso!.descargadas / Math.max(progreso!.total, 1)) * 100)}%`} />
          </Box>
          <Text fontSize="10px" color="#857b71" mt={1}>
            {progreso!.descargadas} de {progreso!.total} páginas
          </Text>
        </Box>
      )}

      {!completado && !enCurso && (
        <Text fontSize="10px" color="#a0aec0" mt={1}>
          Se descarga a pedido: INGEMMET entrega una imagen por página.
        </Text>
      )}
    </Box>
  )
}

function Dato({ etiqueta, children }: { etiqueta: string; children: React.ReactNode }) {
  return (
    <Flex gap={2} mb={1.5} flexDirection={{ base: 'column', md: 'row' }}>
      <Text fontSize="11px" color="#a0aec0" textTransform="uppercase" letterSpacing="0.05em"
        fontWeight="700" minW="120px" flexShrink={0}>
        {etiqueta}
      </Text>
      {/* Box (div), no Text (p): los valores traen Flex/Text en bloque y un <div> dentro de
          un <p> es HTML inválido (React lo reporta en consola). */}
      <Box fontSize="13px" color="#2d3748" fontWeight="500">{children}</Box>
    </Flex>
  )
}
